import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styles from './LocationList.module.css';
import { BreathAPIs } from '../../../../APIs/BreathAPIs';
import { MdOutlineLocationOn } from 'react-icons/md';
import { IoReturnDownBack } from 'react-icons/io5';
import BookingsBar from '../MapView/components/BookingsBar/BookingsBar';

const roundTimeToNearest15Minutes = () => {
  const now = new Date();
  const roundedMinutes = Math.floor(now.getMinutes() / 15) * 15;
  now.setMinutes(roundedMinutes);
  now.setSeconds(0);
  return now;
};

const PATH_STORAGE_KEY = 'publicLocationBrowserPath';
const PATH_TOKEN_KEY = 'publicLocationBrowserPathToken';

// Guests share one pool account across every login, so only resume a saved
// drill-down position if it was saved under this exact token - a different
// login (even the same guest) starts back at "All", same rule as the
// selectedLocation/selectedLocationToken pairing in LocationList.js.
const getStoredPath = () => {
  try {
    const savedToken = localStorage.getItem(PATH_TOKEN_KEY);
    const currentToken = localStorage.getItem('token');
    if (savedToken && currentToken && savedToken === currentToken) {
      const saved = JSON.parse(localStorage.getItem(PATH_STORAGE_KEY) || '[]');
      if (Array.isArray(saved)) return saved;
    }
  } catch (error) {
    // ignore malformed/unavailable storage
  }
  return [];
};

// Guest-only browser for public locations, organized as a state > city
// drill-down instead of one flat list - guests have no "My Locations"/favorites,
// so this replaces the flat "Public" tab for them. Only shows locations
// explicitly flagged guestUserAccessible:true (GET /api/location/guestAccessibleLocations),
// grouped client-side by address.state/city.
const GuestLocationBrowser = ({ selectedLocationId, timeFormatAmPm, onPreviewLocationChange, onBreadcrumbChange }) => {
  const [allLocations, setAllLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [path, setPathState] = useState(getStoredPath); // e.g. ['CA'], ['CA', 'SF']

  // Picking a location from the list doesn't confirm it right away - it first
  // previews that location's chargers/availability (same data the map pin
  // popup shows) via onPreviewLocationChange, and the parent renders its own
  // Continue button (outside this panel) to actually confirm the selection.
  const [previewLocation, setPreviewLocation] = useState(null);
  const [previewChargers, setPreviewChargers] = useState([]);
  const [isChargersLoading, setIsChargersLoading] = useState(false);

  const setPath = (newPath) => {
    setPathState(newPath);
    try {
      localStorage.setItem(PATH_STORAGE_KEY, JSON.stringify(newPath));
      localStorage.setItem(PATH_TOKEN_KEY, localStorage.getItem('token'));
    } catch (error) {
      // ignore storage errors (e.g. private browsing quota)
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchLocations = async () => {
      setIsLoading(true);
      try {
        const res = await BreathAPIs.getGuestAccessibleLocations();
        if (!cancelled) {
          setAllLocations(res?.data || []);
        }
      } catch (error) {
        console.error('Error fetching guest-accessible locations for browser:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchLocations();
    return () => { cancelled = true; };
  }, []);

  // Always resume at the currently selected location's own state/city, not
  // wherever browsing last left off - e.g. clicking a breadcrumb to look
  // around without picking anything new shouldn't make the next open forget
  // where the actual selection lives.
  useEffect(() => {
    if (!isLoading && selectedLocationId && allLocations.length > 0) {
      const selected = allLocations.find((loc) => loc._id === selectedLocationId);
      if (selected?.address?.state && selected?.address?.city) {
        setPath([selected.address.state, selected.address.city]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allLocations, selectedLocationId]);

  const [state, city] = path;

  const scopedLocations = allLocations.filter((loc) => {
    if (state && loc.address?.state !== state) return false;
    if (city && loc.address?.city !== city) return false;
    return true;
  });

  const uniqueValues = (list, key) => {
    const seen = new Set();
    const result = [];
    list.forEach((loc) => {
      const value = loc.address?.[key];
      if (value && !seen.has(value)) {
        seen.add(value);
        result.push(value);
      }
    });
    return result.sort();
  };

  // Each of these replaces this component's own DOM (swapping the folder list)
  // synchronously on click. The document-level "click outside to close" listener
  // in LocationList.js runs after that re-render and checks containerRef against
  // event.target - which is now a detached node - so it wrongly treats this as
  // an outside click and closes the whole menu. Stopping propagation here keeps
  // that click from ever reaching that listener.
  const goInto = (event, value) => {
    event.stopPropagation();
    setPath([...path, value]);
  };
  const goToDepth = (event, depth) => {
    event.stopPropagation();
    setPath(path.slice(0, depth));
  };

  const breadcrumb = (
    <div className={styles['location-browser-breadcrumb']}>
      <span onClick={(e) => goToDepth(e, 0)} className={path.length === 0 ? styles['active-crumb'] : ''}>All</span>
      {path.map((segment, index) => (
        <React.Fragment key={segment}>
          <span> / </span>
          <span onClick={(e) => goToDepth(e, index + 1)} className={index === path.length - 1 ? styles['active-crumb'] : ''}>
            {segment}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  // The breadcrumb/current-location strip is reported up so parents can render
  // it outside this panel instead of inside the gray card - it still needs to
  // live in this component since it drives (and reacts to) path/previewLocation.
  useEffect(() => {
    if (!onBreadcrumbChange) return;
    if (previewLocation) {
      onBreadcrumbChange(
        <div className={styles['location-browser-breadcrumb']}>
          <span className={styles['active-crumb']}>{previewLocation.locationName}</span>
        </div>
      );
    } else if (!isLoading) {
      onBreadcrumbChange(breadcrumb);
    } else {
      onBreadcrumbChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, previewLocation, isLoading]);

  const previewChargersForLocation = (event, loc) => {
    event.stopPropagation();
    setPreviewLocation(loc);
    onPreviewLocationChange && onPreviewLocationChange(loc);
    setPreviewChargers([]);
    setIsChargersLoading(true);
    BreathAPIs.getChargerByLocation(loc._id, roundTimeToNearest15Minutes())
      .then((res) => {
        const sortedChargers = (res?.data?.availableConnectors || [])
          .slice()
          .sort((a, b) => a.charger.orderingNumber - b.charger.orderingNumber);
        setPreviewChargers(sortedChargers);
      })
      .catch((error) => {
        console.error('Error fetching chargers for location preview:', error);
        setPreviewChargers([]);
      })
      .finally(() => setIsChargersLoading(false));
  };

  if (previewLocation) {
    return (
      <>
        <div
          className={`${styles['location-item']} ${styles['flush']}`}
          onClick={(e) => { e.stopPropagation(); setPreviewLocation(null); onPreviewLocationChange && onPreviewLocationChange(null); }}
        >
          <IoReturnDownBack />
          <span className={styles['name']}>Back to locations</span>
        </div>
        {isChargersLoading ? (
          <div className={styles['no-locations']}>Loading chargers...</div>
        ) : previewChargers.length === 0 ? (
          <div className={styles['no-locations']}>No chargers are available.</div>
        ) : (
          previewChargers.map((charger) => (
            <BookingsBar key={charger._id} timeFormatAmPm={timeFormatAmPm} connector={charger} />
          ))
        )}
      </>
    );
  }

  if (isLoading) {
    return <div className={styles['no-locations']}>Loading locations...</div>;
  }

  // Deepest level (state + city chosen) - show the actual locations
  if (path.length === 2) {
    return (
      <>
        {scopedLocations.length === 0 ? (
          <div className={styles['no-locations']}>No locations available</div>
        ) : (
          scopedLocations.map((loc) => (
            <div
              className={`${styles['location-item']} ${styles['flush']} ${selectedLocationId === loc._id ? styles['selected'] : ''}`}
              key={loc._id}
              onClick={(e) => previewChargersForLocation(e, loc)}
            >
              <span className={styles['name']}>{loc.locationName}</span>
              <span className={styles['public-tag']}>Public</span>
            </div>
          ))
        )}
      </>
    );
  }

  // Folder levels: states, then cities
  const folderKey = path.length === 0 ? 'state' : 'city';
  const folders = uniqueValues(scopedLocations, folderKey);

  return (
    <>
      {folders.length === 0 ? (
        <div className={styles['no-locations']}>No locations available</div>
      ) : (
        folders.map((folder) => (
          <div
            className={`${styles['location-item']} ${styles['flush']}`}
            key={folder}
            onClick={(e) => goInto(e, folder)}
          >
            <MdOutlineLocationOn className={styles['folder-icon']} />
            <span className={styles['name']}>{folder}</span>
          </div>
        ))
      )}
      {path.length > 0 && (
        <div className={`${styles['location-item']} ${styles['flush']}`} onClick={(e) => goToDepth(e, path.length - 1)}>
          <IoReturnDownBack />
          <span className={styles['name']}>Back</span>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  timeFormatAmPm: state.auth && state.auth.user ? state.auth.user.timeFormatAmPm : true,
});

export default connect(mapStateToProps)(GuestLocationBrowser);
